import React, { useMemo } from 'react'

import { Text } from '@chakra-ui/react'
import { Colors } from '@qirapagos/lib/theme'
import { formatDateWithMonth } from '@qirapagos/lib/utils/common'
import {
  BoxContainer,
  BoxIcon,
  BoxLeftInfo,
  BoxLeftText,
  BoxRightInfo,
  BoxRightText,
  CancelText,
  PendingText
} from './styles'
import TransferPending from '@qirapagos/lib/assets/svg/TransferPending'
import { InvestmentOrderType, InvestmentStatus } from './types'
import TransferSend from '@qirapagos/lib/assets/svg/TransferSend'
import TransferRejected from '@qirapagos/lib/assets/svg/TransferRejected'
import TransferReceive from '@qirapagos/lib/assets/svg/TransferReceive'
import RescueRejected from '@qirapagos/lib/assets/svg/RescueRejected'
import RescuePending from '@qirapagos/lib/assets/svg/RescuePending'
import { ActivityMovement } from 'store/activity/types'
import { formatCurrency } from '@qirapagos/lib/utils/moneyUtils'

export const getOperationIcon = (movementType: string, status?: number) => {
  const isSubscription = movementType === InvestmentOrderType.SUBSCRIPTION
  const executedIcon = isSubscription ? <TransferSend /> : <TransferReceive />
  const pendingIcon = isSubscription ? <TransferPending /> : <RescuePending />
  const rejectedIcon = isSubscription
    ? (
      <TransferRejected />
      )
    : (
      <RescueRejected />
      )

  switch (status) {
    case InvestmentStatus.Executed:
      return executedIcon
    case InvestmentStatus.Pending:
    case InvestmentStatus.ManualAction:
    case InvestmentStatus.NotSendedInvera:
      return pendingIcon
    case InvestmentStatus.Rejected:
    case InvestmentStatus.Canceled:
      return rejectedIcon
    default:
      return pendingIcon
  }
}

interface Props {
  investment: ActivityMovement
}

const InvestmentActivityItem: React.FC<Props> = ({ investment }) => {
  const operationIcon = useMemo(
    () => getOperationIcon(investment.movementType, investment.status),
    [investment.movementType, investment.status]
  )

  const renderedSubtitle = useMemo(() => {
    let subtitle = ''

    if (investment.status === InvestmentStatus.Executed) {
      subtitle =
        investment.movementType === InvestmentOrderType.SUBSCRIPTION
          ? 'Suscribiste'
          : 'Rescataste'
    } else {
      if (investment.movementType === InvestmentOrderType.SUBSCRIPTION) {
        subtitle =
          investment.status === InvestmentStatus.Rejected
            ? 'Suscripción rechazada'
            : investment.status === InvestmentStatus.Canceled
              ? 'Suscripción cancelada'
              : 'Suscripción pendiente'
      } else {
        subtitle =
          investment.status === InvestmentStatus.Rejected
            ? 'Rescate rechazado'
            : investment.status === InvestmentStatus.Canceled
              ? 'Rescate cancelado'
              : 'Rescate pendiente'
      }
    }

    if (
      investment.status === InvestmentStatus.Rejected ||
      investment.status === InvestmentStatus.Canceled
    ) {
      return <CancelText>{subtitle}</CancelText>
    }

    if (
      investment.status === InvestmentStatus.Pending ||
      investment.status === InvestmentStatus.ManualAction ||
      investment.status === InvestmentStatus.NotSendedInvera
    ) {
      return <PendingText>{subtitle}</PendingText>
    }

    return (
      <Text fontWeight={400} fontSize="14px" align="left">
        {subtitle}
      </Text>
    )
  }, [investment.status, investment.movementType])

  return (
    <BoxContainer>
      <BoxLeftInfo>
        <BoxIcon>{operationIcon}</BoxIcon>
        <BoxLeftText>
          <Text fontWeight={600} fontSize={{ md: '14px', lg: '16px' }}>
            $
            {formatCurrency(investment.amount)}
          </Text>
          {renderedSubtitle}
        </BoxLeftText>
      </BoxLeftInfo>
      <BoxRightInfo>
        <BoxRightText>
          <Text
            fontWeight={400}
            fontSize="14px"
            color={Colors.gray}
            lineHeight="12px"
            mb={2}
            alignSelf="center"
          >
            {formatDateWithMonth(investment.date?.toString() ?? '')}
          </Text>
        </BoxRightText>
      </BoxRightInfo>
    </BoxContainer>
  )
}

export default InvestmentActivityItem
